import { DeviceUtilsService } from './../../shared/services/device-utils.service';
import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, first, map, mergeMap, switchMap } from 'rxjs/operators';
import * as fromMessageActions from './actions';
import * as fromMessageSelectors from './selectors';
import * as fromConversationSelectors from '../conversation/selectors';
import * as fromConversationActions from '../conversation/actions';
import { MessageService } from '../../shared/services/message.service';
import { Update } from '@ngrx/entity';
import {
  Conversation,
  Message,
  MessageStatusType,
} from '@repo/shared/src/services/messaging-service/messaging-service.types';
import { Store } from '@ngrx/store';
import { AppState } from '../../store';
import { NewConversationService } from '../../shared/services/new-conversation.service';

@Injectable()
export class MessageEffects {
  getMessageList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMessageActions.getMessageList, fromMessageActions.cancelGetMessageList),
      switchMap(action => {
        if (action.type === '[Message] Cancel Get Message List') {
          return of(fromMessageActions.cancelGetMessageListSuccess());
        } else {
          return this.messageService.getMessageList(action.conversationId).pipe(
            map(messageResponse => {
              const messages$ = this.store.select(fromMessageSelectors.selectMessageList);

              let isSameResult = false;

              let isAllMessagesRead = false;
              const messageNotRead = messageResponse.data.messages.find(element => !element.clientReadDate);
              isAllMessagesRead = messageNotRead ? false : true;

              messages$
                .subscribe(messages => {
                  if (
                    messages[messages.length - 1]?.id ===
                      messageResponse.data.messages[messageResponse.data.messages.length - 1]?.id &&
                    messages[messages.length - 1]?.status ===
                      messageResponse.data.messages[messageResponse.data.messages.length - 1]?.status &&
                    messages.length !== 0
                  ) {
                    isSameResult = true;
                  }
                })
                .unsubscribe();

              if (isSameResult) {
                return fromMessageActions.cancelGetMessageListSuccess();
              } else {
                if (!isAllMessagesRead) {
                  this.store.dispatch(
                    fromConversationActions.updateConversation({
                      changes: {
                        isRead: true,
                      },
                      conversationId: action.conversationId,
                    }),
                  );
                }

                return fromMessageActions.getMessageListSuccess({
                  messageList: messageResponse.data.messages,
                  conversationId: action.conversationId,
                });
              }
            }),
            catchError(error =>
              of(
                fromMessageActions.getMessageListFailed({
                  error: error.message,
                  conversationId: action.conversationId,
                  isLoadingNeeded: action.isLoadingNeeded,
                }),
              ),
            ),
          );
        }
      }),
    ),
  );

  postMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMessageActions.postMessage),
      mergeMap(action => {
        this.store.dispatch(fromConversationActions.cancelGetConversationList());
        let idMessagesLoaded: string | null;
        this.store
          .select(fromMessageSelectors.idMessagesLoaded)
          .subscribe(id => {
            if (id) {
              idMessagesLoaded = id;
              this.store.dispatch(fromMessageActions.cancelGetMessageList());
            }
          })
          .unsubscribe();

        return this.messageService.postMessage(action.message, action.conversationId).pipe(
          map(messageResponse => {
            let isSameConversation = false;
            this.store
              .select(fromConversationSelectors.getConversationSelected)
              .pipe(first())
              .subscribe((conversationSelected: Conversation) => {
                let isMobile = false;
                this.deviceUtilsService.isMobile$
                  .subscribe(resultIsMobile => {
                    isMobile = resultIsMobile;
                  })
                  .unsubscribe();
                if (
                  conversationSelected?.id === action.conversationId ||
                  (idMessagesLoaded === action.conversationId && isMobile)
                ) {
                  isSameConversation = true;
                }
              });
            return fromMessageActions.postMessageSuccess({
              message: messageResponse.data,
              conversationId: action.conversationId,
              isPostFromDraft: action.isPostFromDraft,
              conversationChanged: isSameConversation ? false : true,
            });
          }),
          catchError(error =>
            of(
              fromMessageActions.postMessageFailed({
                error: action.message.status !== MessageStatusType.Draft ? error.message : null,
              }),
            ),
          ),
        );
      }),
    ),
  );

  postMessageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMessageActions.postMessageSuccess),
      map(action => {
        this.store.dispatch(fromConversationActions.cancelGetConversationList());

        const update: Update<Conversation> = {
          id: action.conversationId,
          changes: {
            lastUpdateDate: action.message.receivedDate,
            lastMessageReceivedContent: action.message.messageContent.text,
            lastMessageStatus: action.message.status,
          },
        };
        return fromConversationActions.updateConversationSuccess({
          conversationUpdated: update,
        });
      }),
    ),
  );

  deleteMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMessageActions.deleteMessage),
      mergeMap(action => {
        this.store.dispatch(fromConversationActions.cancelGetConversationList());
        this.store
          .select(fromMessageSelectors.idMessagesLoaded)
          .subscribe(id => {
            if (id) {
              this.store.dispatch(fromMessageActions.cancelGetMessageList());
            }
          })
          .unsubscribe();

        return this.messageService.deleteMessage(action.messageId).pipe(
          map(() => {
            const update: Update<Conversation> = {
              id: action.conversationId,
              changes: {
                lastMessageStatus: action.lastMessage.status,
                lastMessageReceivedContent: action.lastMessage.messageContent.text,
                lastUpdateDate: new Date().getTime(),
              },
            };
            return fromMessageActions.deleteMessageSuccess({
              messageId: action.messageId,
              updateConversation: update,
            });
          }),
        );
      }),
    ),
  );

  deleteMessageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMessageActions.deleteMessageSuccess),
      map(action => {
        this.store.dispatch(fromConversationActions.cancelGetConversationList());
        this.store
          .select(fromMessageSelectors.idMessagesLoaded)
          .subscribe(id => {
            if (id) {
              this.store.dispatch(fromMessageActions.cancelGetMessageList());
            }
          })
          .unsubscribe();

        return fromConversationActions.updateConversation({
          changes: action.updateConversation.changes,
          conversationId: action.updateConversation.id.toString(),
        });
      }),
    ),
  );

  patchMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMessageActions.patchMessage),
      switchMap(action => {
        this.store.dispatch(fromConversationActions.cancelGetConversationList());
        this.store
          .select(fromMessageSelectors.idMessagesLoaded)
          .subscribe(id => {
            if (id) {
              this.store.dispatch(fromMessageActions.cancelGetMessageList());
            }
          })
          .unsubscribe();

        return this.messageService.patchMessage(action.message, action.messageId).pipe(
          map(() => {
            const update: Update<Message> = {
              id: action.messageId,
              changes: {
                // TODO : refact after update status draft in BFF, ( use action.message status)
                status: action.message.status ? action.message.status : MessageStatusType.Draft,
                messageContent: {
                  attachments: action.message.messageContent.attachments,
                  audioMessage: action.message.messageContent.audioMessage,
                  html: action.message.messageContent.html,
                  text: action.message.messageContent.text,
                },
              },
            };

            if (!action.isPatchFromDraft) {
              const dateNow = new Date().getTime();

              update.changes.clientReadDate = dateNow;
              update.changes.receivedDate = dateNow;
            }

            return fromMessageActions.patchMessageSuccess({
              message: update,
              conversationId: action.conversationId,
              isPatchFromDraft: action.isPatchFromDraft,
            });
          }),
          catchError(error =>
            of(
              fromMessageActions.patchMessageFailed({
                error: action.message.status !== MessageStatusType.Draft ? error.message : null,
              }),
            ),
          ),
        );
      }),
    ),
  );

  patchMessageSucces$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMessageActions.patchMessageSuccess),
      map(action => {
        this.store.dispatch(fromConversationActions.cancelGetConversationList());
        this.store
          .select(fromMessageSelectors.idMessagesLoaded)
          .subscribe(id => {
            if (id) {
              this.store.dispatch(fromMessageActions.cancelGetMessageList());
            }
          })
          .unsubscribe();

        const update: Update<Conversation> = {
          id: action.conversationId,
          changes: {
            lastMessageReceivedContent: action.message.changes.messageContent?.text,
            lastMessageStatus: action.message.changes.status,
          },
        };

        if (action.message.changes.clientReadDate) {
          update.changes.lastUpdateDate = action.message.changes.clientReadDate;
        }

        return fromConversationActions.updateConversationSuccess({
          conversationUpdated: update,
        });
      }),
    ),
  );

  unsetIdMessagesLoaded$ = createEffect(() => {
    return this.newConversationService.newConversation$.pipe(
      switchMap(newConversation => {
        return newConversation ? of(fromMessageActions.unsetIdMessagesLoaded()) : [];
      }),
    );
  });

  unsetListMessage$ = createEffect(() => {
    return this.newConversationService.newConversation$.pipe(
      switchMap(newConversation => {
        return newConversation ? of(fromMessageActions.unsetListMessage()) : [];
      }),
    );
  });

  constructor(
    private actions$: Actions,
    private newConversationService: NewConversationService,
    private messageService: MessageService,
    private deviceUtilsService: DeviceUtilsService,
    private readonly store: Store<AppState>,
  ) {}
}
